/**
 * @file Per-instance state of one `enemy_ranged_01` agent.
 *
 * Pure data, like every component: the machine that reads and writes it is
 * {@link ../systems/EnemyRangedAISystem.js}. Everything an agent needs to run
 * independently of its neighbours — its own state, destination, target,
 * cooldowns, counters and pattern — lives on this object, which is what makes
 * a hundred simultaneous instances behave as a hundred separate enemies.
 */

import { Component } from '../ecs/Component.js';
import { resolveRangedConfig, RangedState } from '../config/EnemyRanged.js';

export class RangedAI extends Component {
  /**
   * @param {object} [options] Instance setup.
   * @param {Partial<import('../config/EnemyRanged.js').RangedTemplateConfig>} [options.config]
   *   Parameter overrides on top of the template defaults.
   * @param {string} [options.instanceName] Label used by warnings and the debug overlay.
   * @param {number} [options.radius] Body radius, used for navigation clearance.
   * @param {number} [options.separationRadius] Personal space kept from other agents.
   * @param {number} [options.separationStrength] Push strength inside that radius.
   */
  constructor({
    config = {},
    instanceName = 'enemy_ranged_01',
    radius = 0.6,
    separationRadius = 2.4,
    separationStrength = 10
  } = {}) {
    super();

    /** @type {string} */
    this.instanceName = instanceName;
    /** @type {import('../config/EnemyRanged.js').RangedTemplateConfig} Validated parameters. */
    this.config = resolveRangedConfig(config, instanceName);
    /** @type {number} */
    this.radius = radius;
    /** @type {number} */
    this.separationRadius = separationRadius;
    /** @type {number} */
    this.separationStrength = separationStrength;

    // --- Machine -------------------------------------------------------------
    /** @type {string} Current state, one of {@link RangedState}. */
    this.state = this.config.starter_state;
    /** @type {string} State the agent was in before the last transition. */
    this.previousState = this.config.starter_state;
    /** @type {number} Seconds spent in the current state. */
    this.timeInState = 0;
    /** @type {boolean} Whether `enter_state` has run for the current state. */
    this.entered = false;

    // --- Spawn anchor --------------------------------------------------------
    /** @type {number} */
    this.spawnX = 0;
    /** @type {number} */
    this.spawnZ = 0;
    /** @type {number} Facing recorded at spawn, used by the static idle scan. */
    this.spawnRotation = 0;

    // --- Target --------------------------------------------------------------
    /** @type {import('../ecs/Entity.js').Entity | null} */
    this.target = null;
    /** @type {boolean} Whether the target is currently visible. */
    this.hasLineOfSight = false;
    /** @type {boolean} Whether the target is inside the vision cone, seen or not. */
    this.targetInCone = false;
    /** @type {boolean} Whether the target was ever acquired in this aggro spell. */
    this.hasLastKnown = false;
    /** @type {number} */
    this.lastKnownX = 0;
    /** @type {number} */
    this.lastKnownZ = 0;
    /** @type {number} Seconds since the target was last seen. */
    this.memoryTimer = 0;
    /** @type {number} Countdown to the next detection sweep. */
    this.detectionTimer = 0;

    // --- Navigation ----------------------------------------------------------
    /** @type {Array<{ x: number, z: number }>} Remaining waypoints. */
    this.path = [];
    /** @type {number} Index of the waypoint currently being walked to. */
    this.pathIndex = 0;
    /** @type {boolean} Whether `destinationX/Z` is meaningful. */
    this.hasDestination = false;
    /** @type {number} */
    this.destinationX = 0;
    /** @type {number} */
    this.destinationZ = 0;
    /** @type {number} Countdown to the next route recalculation. */
    this.repathTimer = 0;
    /** @type {number} Countdown to the next idle destination roll. */
    this.selectTimer = 0;
    /** @type {number} Seconds spent barely moving while a route was active. */
    this.stuckTimer = 0;
    /** @type {number} */
    this.lastX = 0;
    /** @type {number} */
    this.lastZ = 0;
    /** @type {number} Distance covered inside the current stuck window. */
    this.stuckDistance = 0;

    // --- Combat --------------------------------------------------------------
    /** @type {string} Active combat pattern. */
    this.pattern = this.config.aggro_default_combat_pattern;
    /** @type {number} Full duration of the current pattern slot. */
    this.patternDuration = 0;
    /** @type {number} Seconds left before the pattern may be re-rolled. */
    this.patternTimer = 0;
    /** @type {number} Strafe direction; flipped on a timer so agents orbit. */
    this.strafeSign = Math.random() < 0.5 ? -1 : 1;
    /** @type {number} */
    this.strafeTimer = 0;
    /** @type {number} Consecutive shots that failed to hit the target. */
    this.failedShots = 0;
    /**
     * Shots in flight, oldest first. Each resolves to a hit or a miss, or times
     * out into a miss once the round can no longer reach anything.
     * @type {number[]}
     */
    this.pendingShots = [];
    /** @type {boolean} Whether the agent wants its weapon firing this frame. */
    this.wantsToFire = false;
    /** @type {boolean} Patterns set this to suppress fire (e.g. while hiding). */
    this.fireBlocked = false;
    /** @type {boolean} Set when consecutive misses force a new attack position. */
    this.forceReposition = false;

    // --- Cover ---------------------------------------------------------------
    /** @type {boolean} */
    this.hasCover = false;
    /** @type {number} */
    this.coverX = 0;
    /** @type {number} */
    this.coverZ = 0;
    /** @type {number} Firing position associated with the current cover. */
    this.peekX = 0;
    /** @type {number} */
    this.peekZ = 0;
    /** @type {number} Countdown to re-evaluating the chosen cover. */
    this.coverTimer = 0;
    /** @type {'hiding' | 'exposed'} Phase of the cover cycle. */
    this.coverPhase = 'exposed';
    /** @type {number} Seconds left in the current cover phase. */
    this.coverPhaseTimer = 0;
    /** @type {number} Shots taken since leaving cover. */
    this.shotsThisCycle = 0;
    /** @type {boolean} Set when cover was searched for and none was found. */
    this.coverUnavailable = false;

    // --- Stun ---------------------------------------------------------------
    /** @type {number} Seconds left of the thrown-weapon stun. */
    this.stunRemaining = 0;
    /** @type {number} Seconds until the next stun-star burst. */
    this.stunFxCooldown = 0;
    /** @type {number} Angle of the spinning stun-star ring. */
    this.stunSpin = 0;
  }

  /**
   * Records where the agent was spawned. Roaming is measured from this point,
   * and the patrol leash — when configured — as well.
   *
   * @param {number} x Spawn X.
   * @param {number} z Spawn Z.
   * @param {number} [rotationY] Spawn facing.
   * @returns {void}
   */
  anchorTo(x, z, rotationY = 0) {
    this.spawnX = x;
    this.spawnZ = z;
    this.spawnRotation = rotationY;
    this.lastX = x;
    this.lastZ = z;
  }

  /** @returns {void} */
  reset() {
    this.state = this.config.starter_state;
    this.previousState = this.config.starter_state;
    this.timeInState = 0;
    this.entered = false;
    this.target = null;
    this.hasLineOfSight = false;
    this.targetInCone = false;
    this.hasLastKnown = false;
    this.memoryTimer = 0;
    this.detectionTimer = 0;
    this.path.length = 0;
    this.pathIndex = 0;
    this.hasDestination = false;
    this.repathTimer = 0;
    this.selectTimer = 0;
    this.stuckTimer = 0;
    this.stuckDistance = 0;
    this.pattern = this.config.aggro_default_combat_pattern;
    this.patternDuration = 0;
    this.patternTimer = 0;
    this.failedShots = 0;
    this.pendingShots.length = 0;
    this.wantsToFire = false;
    this.fireBlocked = false;
    this.forceReposition = false;
    this.hasCover = false;
    this.coverTimer = 0;
    this.coverPhase = 'exposed';
    this.coverPhaseTimer = 0;
    this.shotsThisCycle = 0;
    this.coverUnavailable = false;
    this.stunRemaining = 0;
    this.stunFxCooldown = 0;
    this.stunSpin = 0;
  }
}

export { RangedState };
